import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import relbaseConfigServices from "app/services/relbase-config-services";
import { timeAgo } from "app/pages/pulse/pulseFormat";

// Como se anula cada tipo de documento. Activar uno incluye vigilar su anulacion.
const ANNULMENT_BY_GROUP = {
    sale_note: "la nota de venta se cancela en Relbase",
    invoice: "una nota de crédito la anula",
    receipt: "una nota de crédito la anula",
};

// Un pedido anulado nunca sale de Haulers ni de su viaje: queda eliminado y, si estaba en un viaje,
// sigue ahi con su entrega cancelada. Lo que cambia segun la etapa es a quien se avisa.
const STAGE_RULES = [
    ["Sin planificar", "queda eliminado"],
    ["Sin iniciar el viaje", "queda eliminado, sigue en el viaje con su entrega cancelada y se avisa"],
    ["Viaje iniciado", "queda eliminado, sigue en el viaje, al chofer le llega el aviso y se avisa a operaciones"],
    ["Pedido entregado o finalizado", "queda eliminado (es una devolución) y se avisa"],
    ["Nota de crédito parcial", "no se elimina: se anota y se avisa"],
];

const DAYS = [0, 1, 3, 7, 14, 30];
const daysLabel = (d) => (d === 0 ? "Solo hoy" : d === 1 ? "Desde ayer" : `Últimos ${d} días`);

const STAGE_LABEL = { pendiente: "Sin planificar", en_preparacion: "Sin iniciar el viaje", en_ruta: "Viaje iniciado", entregado: "Pedido entregado o finalizado" };

const Section = ({ n, title, subtitle, children }) => (
    <Card variant="outlined">
        <CardContent>
            <Typography variant="h5" sx={{ mb: 0.25 }}>{n}. {title}</Typography>
            {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{subtitle}</Typography>}
            {children}
        </CardContent>
    </Card>
);

const toForm = (data) => {
    const c = data.config;
    return {
        document_types: c.document_types,
        skip_channel_ids: c.skip_channel_ids,
        include_new_channels: c.include_new_channels,
        skip_if_label_matches: c.skip_if_label_matches,
        pickup_mode: c.pickup_mode,
        alert_emails: (c.alert_emails || []).join(", "),
    };
};

// Configuracion de Relbase de un cliente. Se abre desde la configuracion del plugin en Plugins; solo Kargotools.
const RelbaseConfig = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authUser } = useJumboAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState([]);
    const [days, setDays] = useState(7);
    // La vista previa corre en el backend (job): se pide, y se muestra la que corresponde a este pedido.
    const [previewToken, setPreviewToken] = useState(null);
    const pollRef = useRef(null);

    const allowed = useMemo(() => {
        const superadmin = (authUser?.profiles || []).some((p) => p.name === "superadmin");
        return superadmin && /@kargotools\.com$/i.test(authUser?.email || "");
    }, [authUser]);

    useEffect(() => {
        if (authUser && !allowed) navigate("/trips");
    }, [authUser, allowed, navigate]);

    const load = useCallback(async () => {
        try {
            const d = await relbaseConfigServices.show(id);
            setData(d);
            setForm((prev) => prev || toForm(d));
            return d;
        } catch (e) {
            enqueueSnackbar("No se pudo cargar la configuración de Relbase", { variant: "error" });
            return null;
        }
    }, [id, enqueueSnackbar]);

    useEffect(() => {
        if (allowed) load();
        return () => clearInterval(pollRef.current);
    }, [allowed, load]);

    // Mientras la vista previa o la primera sincronizacion corren, se refresca el estado cada 4 s.
    const syncStatus = data?.initial_sync?.status;
    const ownPreview = previewToken && data?.preview?.token === previewToken ? data.preview : null;
    const previewing = !!previewToken && (!ownPreview || ownPreview.status === "running");
    const preview = ownPreview?.status === "done" ? ownPreview.result : null;
    useEffect(() => {
        clearInterval(pollRef.current);
        if (syncStatus === "queued" || syncStatus === "running" || previewing) {
            pollRef.current = setInterval(load, 4000);
        }
        return () => clearInterval(pollRef.current);
    }, [syncStatus, previewing, load]);

    const channelName = useMemo(() => {
        const m = {};
        (data?.channels || []).forEach((c) => { m[c.id] = c.name; });
        return m;
    }, [data]);

    const payloadConfig = useCallback(() => ({
        document_types: form.document_types,
        skip_channel_ids: form.skip_channel_ids,
        include_new_channels: form.include_new_channels,
        known_channel_ids: (data?.channels || []).map((c) => c.id),
        skip_if_label_matches: form.skip_if_label_matches,
        pickup_mode: form.pickup_mode,
        alert_emails: form.alert_emails.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean),
    }), [form, data]);

    const combined = form && form.document_types.includes("sale_note") && form.document_types.some((t) => t === "invoice" || t === "receipt");

    const dirty = useMemo(() => {
        if (!data || !form) return false;
        const base = toForm(data);
        const norm = (f) => JSON.stringify({ ...f, document_types: [...f.document_types].sort(), skip_channel_ids: [...f.skip_channel_ids].sort() });
        return norm(base) !== norm(form);
    }, [data, form]);

    const save = async () => {
        setSaving(true);
        setErrors([]);
        try {
            const d = await relbaseConfigServices.update(id, payloadConfig());
            setData(d);
            setForm(toForm(d));
            enqueueSnackbar("Configuración guardada", { variant: "success" });
            return true;
        } catch (e) {
            setErrors(e?.response?.data?.errors || ["No se pudo guardar"]);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const runPreview = async () => {
        setErrors([]);
        try {
            const d = await relbaseConfigServices.preview(id, { sinceDays: days, config: payloadConfig() });
            setData(d);
            setPreviewToken(d.preview?.token || null);
        } catch (e) {
            setErrors(e?.response?.data?.errors || ["No se pudo pedir la vista previa"]);
        }
    };
    const clearPreview = () => setPreviewToken(null);

    const saveAndImport = async () => {
        if (dirty && !(await save())) return;
        try {
            const d = await relbaseConfigServices.initialSync(id, days);
            setData(d);
            clearPreview();
            enqueueSnackbar("Importación encolada", { variant: "success" });
        } catch (e) {
            enqueueSnackbar("No se pudo encolar la importación", { variant: "error" });
        }
    };

    const toggleType = (key) => {
        setForm((f) => ({
            ...f,
            document_types: f.document_types.includes(key) ? f.document_types.filter((t) => t !== key) : [...f.document_types, key],
        }));
        clearPreview();
    };

    const toggleChannel = (cid) => {
        setForm((f) => ({
            ...f,
            skip_channel_ids: f.skip_channel_ids.includes(cid) ? f.skip_channel_ids.filter((c) => c !== cid) : [...f.skip_channel_ids, cid],
        }));
        clearPreview();
    };

    if (!allowed) return null;
    if (!data || !form) return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress color="warning" /></Stack>;

    const today = (data.sync_stats || {})[moment().format("YYYY-MM-DD")] || {};
    const sync = data.initial_sync;

    return (
        <Stack spacing={2} sx={{ maxWidth: 1100 }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate("/plugins")}>Plugins</Button>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                <Typography variant="h2" sx={{ mb: 0 }}>Relbase · {data.company.name}</Typography>
                <Chip size="small" color={data.enabled ? "success" : "default"} label={data.enabled ? "Activa" : "Apagada"} />
                {!data.configured && <Chip size="small" variant="outlined" label="Sin configurar: solo notas de venta" />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
                Qué documentos de Relbase entran a Haulers como pedidos, de qué canales de venta, y qué pasa si después se anulan.
                Se lee cada 3 minutos{data.last_run_at ? ` · última lectura ${timeAgo(data.last_run_at)}` : ""}.
            </Typography>

            {errors.length > 0 && <Alert severity="error">{errors.map((e, i) => <div key={i}>{e}</div>)}</Alert>}

            <Section n={1} title="Documentos a importar" subtitle="Cada documento activado incluye vigilar su anulación.">
                <Stack spacing={0.5}>
                    {data.groups.map((g) => (
                        <Stack key={g.key} direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }}>
                            <FormControlLabel sx={{ minWidth: 220 }} control={<Checkbox checked={form.document_types.includes(g.key)} onChange={() => toggleType(g.key)} />} label={g.label} />
                            <Typography variant="body2" color="text.secondary">Se anula cuando {ANNULMENT_BY_GROUP[g.key]}</Typography>
                        </Stack>
                    ))}
                </Stack>
                {combined && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Notas de venta y facturas o boletas no se combinan: Relbase no liga la nota de venta con la factura que sale de ella, y la misma venta entraría dos veces.
                    </Alert>
                )}
            </Section>

            <Section n={2} title="Canales de venta" subtitle="Los lee de Relbase y son de cada cliente. Marcado = entra a Haulers.">
                {data.channels.length === 0 && <Alert severity="warning">No se pudo leer la lista de canales de Relbase.</Alert>}
                <Stack spacing={0.25}>
                    {data.channels.map((c) => (
                        <Stack key={c.id} direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }}>
                            <FormControlLabel sx={{ minWidth: 320 }}
                                              control={<Checkbox checked={!form.skip_channel_ids.includes(c.id)} onChange={() => toggleChannel(c.id)} />}
                                              label={<span>{c.name} <Typography component="span" variant="caption" color="text.secondary">#{c.id}{c.active ? "" : " · inactivo"}</Typography></span>} />
                            {c.warning && (
                                <Typography variant="body2" color={form.skip_channel_ids.includes(c.id) ? "text.secondary" : "warning.dark"}>
                                    ⚠ {c.warning}{form.skip_channel_ids.includes(c.id) ? "" : ": importarlo duplica pedidos"}
                                </Typography>
                            )}
                        </Stack>
                    ))}
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={0.5}>
                    <FormControlLabel control={<Switch checked={form.include_new_channels} onChange={(e) => setForm({ ...form, include_new_channels: e.target.checked })} />}
                                      label="Los canales que se creen después en Relbase entran solos" />
                    <FormControlLabel control={<Switch checked={form.skip_if_label_matches} onChange={(e) => setForm({ ...form, skip_if_label_matches: e.target.checked })} />}
                                      label="Resguardo: omitir el documento si su número de pedido ya existe en Haulers como Shopify o Mercado Libre" />
                </Stack>
            </Section>

            <Section n={3} title="Retiros en tienda" subtitle='Se reconocen por "retiro" o "retira" en el comentario del documento.'>
                <RadioGroup value={form.pickup_mode} onChange={(e) => setForm({ ...form, pickup_mode: e.target.value })}>
                    <FormControlLabel value="import" control={<Radio />} label="Importar como retiro: se preparan, pero no entran a ruta" />
                    <FormControlLabel value="skip" control={<Radio />} label="No importar" />
                </RadioGroup>
            </Section>

            <Section n={4} title="Anulaciones" subtitle="Un pedido anulado nunca desaparece de Haulers: queda eliminado y, si estaba en un viaje, sigue ahí con su entrega cancelada.">
                <Table size="small" sx={{ mb: 2, maxWidth: 760 }}>
                    <TableBody>
                        {STAGE_RULES.map(([stage, rule]) => (
                            <TableRow key={stage}>
                                <TableCell sx={{ width: 320 }}>{stage}</TableCell>
                                <TableCell>{rule}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TextField fullWidth size="small" label="Avisar a (correos separados por coma)" value={form.alert_emails}
                           onChange={(e) => setForm({ ...form, alert_emails: e.target.value })}
                           helperText={`Si queda vacío, avisa a ${data.default_alert_emails.join(", ")}`} />
            </Section>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
                {dirty && <Typography variant="body2" color="warning.dark" sx={{ alignSelf: "center" }}>Hay cambios sin guardar</Typography>}
                <Button variant="contained" disabled={!dirty || saving || combined || form.document_types.length === 0} onClick={save}>
                    {saving ? "Guardando…" : "Guardar configuración"}
                </Button>
            </Stack>

            <Section n={5} title="Primera sincronización"
                     subtitle="Trae documentos hacia atrás con los filtros de arriba. Sin esto, un tipo recién activado parte desde el documento más nuevo y no importa historia.">
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                    <TextField select size="small" label="Traer documentos" value={days} onChange={(e) => { setDays(e.target.value); clearPreview(); }} sx={{ minWidth: 200 }}>
                        {DAYS.map((d) => <MenuItem key={d} value={d}>{daysLabel(d)}</MenuItem>)}
                    </TextField>
                    <Button variant="outlined" disabled={previewing || combined || form.document_types.length === 0} onClick={runPreview}>
                        {previewing ? <><CircularProgress size={16} sx={{ mr: 1 }} />Leyendo Relbase…</> : "Vista previa"}
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" color="warning" disabled={!preview || syncStatus === "queued" || syncStatus === "running"} onClick={saveAndImport}>
                        {dirty ? "Guardar e importar" : "Importar"}
                    </Button>
                </Stack>

                {sync && (
                    <Alert sx={{ mt: 1.5 }} severity={sync.status === "error" ? "error" : sync.status === "done" ? "success" : "info"}
                           icon={sync.status === "queued" || sync.status === "running" ? <CircularProgress size={18} /> : undefined}>
                        {sync.status === "queued" && `Importación en cola (${daysLabel(sync.since_days)}), pedida por ${sync.by || "-"}.`}
                        {sync.status === "running" && `Importando… empezó ${timeAgo(sync.started_at)}.`}
                        {sync.status === "done" && `Última importación ${timeAgo(sync.finished_at)}: ${sync.imported} importados, ${sync.skipped} omitidos.`}
                        {sync.status === "error" && `La importación falló ${timeAgo(sync.finished_at)}: ${sync.error}`}
                    </Alert>
                )}

                {ownPreview?.status === "error" && <Alert severity="error" sx={{ mt: 1.5 }}>La vista previa falló: {ownPreview.error}</Alert>}
                {previewing && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        Calculando la vista previa{ownPreview?.requested_at ? ` (pedida ${timeAgo(ownPreview.requested_at)})` : ""}: recorre los documentos de Relbase de a 12 por página, así que con muchos documentos puede tardar unos minutos.
                    </Typography>
                )}
                {preview && (
                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                            <Chip color="success" label={`Se importarán ${preview.import}`} />
                            {Object.entries(preview.by_type).map(([k, v]) => (
                                <Chip key={k} variant="outlined" label={`${v} ${(data.groups.find((g) => g.key === k)?.label || k).toLowerCase()}`} />
                            ))}
                            <Chip variant="outlined" label={`${preview.skipped} omitidos`} />
                        </Stack>
                        {Object.keys(preview.by_reason).length > 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Omitidos: {Object.entries(preview.by_reason).map(([r, n]) => `${n} ${(preview.reason_labels?.[r] || r).toLowerCase()}`).join(" · ")}
                            </Typography>
                        )}
                        {preview.stale > 0 && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                {preview.stale} de los que entran tienen más de 2 días: probablemente ya se despacharon por otro lado y entrarían como pendientes de despacho. Si es así, achica la ventana.
                            </Alert>
                        )}
                        {preview.errors?.length > 0 && <Alert severity="error" sx={{ mb: 1 }}>{preview.errors.join(" · ")}</Alert>}
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Canal</TableCell>
                                        <TableCell align="right">Entran</TableCell>
                                        <TableCell>Detalle</TableCell>
                                        <TableCell align="right">Monto</TableCell>
                                        <TableCell align="right">Más de 2 días</TableCell>
                                        <TableCell>Omitidos</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {channelRows(preview).map(([cid, c]) => (
                                        <TableRow key={cid} sx={{ opacity: c.import ? 1 : 0.6 }}>
                                            <TableCell><b>{cid === "sin_canal" ? "Sin canal" : channelName[cid] || `Canal ${cid}`}</b></TableCell>
                                            <TableCell align="right"><b>{c.import}</b></TableCell>
                                            <TableCell>{Object.entries(c.by_type).map(([g, n]) => `${n} ${(data.groups.find((x) => x.key === g)?.label || g).toLowerCase()}`).join(" · ") || "-"}</TableCell>
                                            <TableCell align="right">{c.import ? `$${Number(c.amount).toLocaleString("es-CL")}` : "-"}</TableCell>
                                            <TableCell align="right">{c.stale ? <Typography variant="body2" color="warning.dark">{c.stale}</Typography> : "-"}</TableCell>
                                            <TableCell>
                                                {c.skipped
                                                    ? <Typography variant="body2" color="text.secondary">{Object.entries(c.by_reason).map(([r, n]) => `${n} ${(preview.reason_labels?.[r] || r).toLowerCase()}`).join(" · ")}</Typography>
                                                    : "-"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Section>

            <Section n={6} title="Estado" subtitle="Lo que pasó hoy en las lecturas de cada 3 minutos.">
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    <Chip color="success" variant="outlined" label={`${today.imported || 0} importados hoy`} />
                    <Chip variant="outlined" label={`${today.skipped || 0} omitidos`} />
                    <Chip color={today.annulled ? "warning" : "default"} variant="outlined" label={`${today.annulled || 0} anulados`} />
                </Stack>

                <Typography variant="h6">Últimos omitidos</Typography>
                {data.recent_skips.length === 0
                    ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Ninguno todavía.</Typography>
                    : (
                        <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow><TableCell>Cuándo</TableCell><TableCell>Documento</TableCell><TableCell>Canal</TableCell><TableCell>Motivo</TableCell></TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.recent_skips.map((s, i) => (
                                        <TableRow key={`${s.id}-${i}`}>
                                            <TableCell>{timeAgo(s.at)}</TableCell>
                                            <TableCell>{s.code || s.folio}{s.label ? ` (#${s.label})` : ""}</TableCell>
                                            <TableCell>{channelName[s.channel] || s.channel || "-"}</TableCell>
                                            <TableCell>{s.reason_label}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                <Typography variant="h6">Anulaciones recientes</Typography>
                {data.annulments.length === 0
                    ? <Typography variant="body2" color="text.secondary">Ninguna todavía.</Typography>
                    : (
                        <Table size="small">
                            <TableHead>
                                <TableRow><TableCell>Cuándo</TableCell><TableCell>Pedido</TableCell><TableCell>Qué la anuló</TableCell><TableCell>Estaba</TableCell><TableCell>Qué hizo Haulers</TableCell></TableRow>
                            </TableHead>
                            <TableBody>
                                {data.annulments.map((a, i) => (
                                    <TableRow key={`${a.order_id}-${i}`}>
                                        <TableCell>{timeAgo(a.registered_at)}</TableCell>
                                        <TableCell>{a.order_code}</TableCell>
                                        <TableCell>{a.kind === "credit_note" ? `Nota de crédito ${a.folio} (${a.scope})` : a.kind === "sale_note_cancelled" ? "Nota de venta cancelada" : "Documento anulado"}</TableCell>
                                        <TableCell>{STAGE_LABEL[a.stage] || a.stage}</TableCell>
                                        <TableCell>{a.action === "eliminado" ? "Quedó eliminado" : "Solo anotado, revisar"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
            </Section>
        </Stack>
    );
};

// Primero los canales que importan algo (de mas a menos), despues los que solo se omiten.
const channelRows = (preview) =>
    Object.entries(preview.by_channel || {}).sort(([, a], [, b]) => (b.import - a.import) || (b.skipped - a.skipped));

export default RelbaseConfig;
